<template>
    <v-container fluid class="page-container">
        <page-header title="Partners" :subtitle="totalLabel">
            <v-btn v-if="isAdmin" color="primary" depressed @click="createItem">
                <v-icon left>mdi-plus</v-icon>New partner
            </v-btn>
        </page-header>

        <filter-panel
            :fields="filterFields"
            :value="listState"
            :loading="loading"
            @apply="applyFilters"
        />

        <v-card outlined>
            <v-data-table
                :headers="visibleHeaders"
                v-bind="tableBindings"
                class="list-table clickable-rows"
                no-data-text="No partners match these filters"
                @update:options="onTableOptions"
                @click:row="openItem($event)"
            >
                <template v-slot:[`item.title`]="{ item }">
                    <span class="font-weight-medium">{{ item.title }}</span>
                    <v-chip v-if="item.b2b" x-small outlined color="secondary" class="ml-2">B2B</v-chip>
                </template>
                <template v-slot:[`item.category`]="{ item }">{{ categoryTitle(item.category) }}</template>
                <template v-slot:[`item.available`]="{ item }">
                    <v-icon small :color="item.available ? 'success' : 'grey'">
                        {{ item.available ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
                    </v-icon>
                </template>
                <template v-slot:[`item.address`]="{ item }">
                    <div class="text-truncate address">{{ item.address }}</div>
                </template>
                <template v-slot:[`item.vatNumbers`]="{ item }">{{ vatNumbers(item.vatNumbers) }}</template>
                <template v-slot:[`item.approved`]="{ item }">
                    <v-chip
                        small
                        :outlined="!item.approved"
                        :color="item.approved ? 'success' : 'error'"
                        :disabled="togglingId === item.id"
                        :title="isAdmin ? 'Click to toggle' : ''"
                        @click.stop="isAdmin && toggleApprove(item)"
                    >
                        {{ item.approved ? 'Approved' : 'Pending' }}
                    </v-chip>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex justify-end">
                        <v-btn icon small color="primary" title="Edit" @click.stop="openItem(item, 'edit')">
                            <v-icon small>mdi-pencil-outline</v-icon>
                        </v-btn>
                        <delete-button
                            v-if="isAdmin"
                            title="Delete this partner?"
                            @delete-entry="deleteItem(item, deletePartner, 'Partner')"
                        />
                    </div>
                </template>
            </v-data-table>
            <table-footer v-model="page" :page-count="pageCount" :total="total" :items-per-page="itemsPerPage" />
        </v-card>
    </v-container>
</template>

<script>
    import partnerService from '@/services/partner-service'
    import dealerService from '@/services/dealer-service'
    import paginatedList from '@/mixins/paginated-list'
    import PageHeader from '@/components/PageHeader'
    import FilterPanel from '@/components/FilterPanel'
    import TableFooter from '@/components/TableFooter'
    import DeleteButton from '@/components/DeleteButton'
    import { fullName } from '@/utils/format'

    export default {
        name: 'PartnerRoute',
        components: { PageHeader, FilterPanel, TableFooter, DeleteButton },
        mixins: [paginatedList],
        data() {
            return {
                categories: null,
                dealers: null,
                togglingId: null,
                headers: [
                    { text: 'Title', value: 'title' },
                    { text: 'Category', value: 'category' },
                    { text: 'Available', value: 'available', align: 'center' },
                    { text: 'Address', value: 'address' },
                    { text: 'VAT numbers', value: 'vatNumbers' },
                    { text: 'Type', value: 'type' },
                    { text: 'Phone', value: 'phone' },
                    { text: 'Email', value: 'email' },
                    { text: 'Status', value: 'approved' },
                    { text: '', value: 'actions', align: 'end', sortable: false, width: 96 }
                ]
            }
        },
        computed: {
            filterFields() {
                const fields = [
                    { key: 'partner', label: 'Partner', placeholder: 'Title or id' },
                    { key: 'address', label: 'Address' },
                    { key: 'category', label: 'Category', type: 'autocomplete', items: this.categories, itemText: 'title' },
                    { key: 'available', label: 'Available', type: 'boolean' },
                    { key: 'b2b', label: 'B2B', type: 'boolean' },
                    { key: 'approved', label: 'Approved', type: 'boolean' }
                ]
                if (this.isAdmin) {
                    fields.push({ key: 'dealer', label: 'Dealer', type: 'autocomplete', items: this.dealers, itemText: fullName })
                }
                return fields
            }
        },
        created() {
            partnerService.getAllCategories().then(resp => {
                this.categories = resp.data.data
            }).catch(() => {
                this.categories = []
            })
            if (this.isAdmin) {
                dealerService.getAllDealers().then(resp => {
                    this.dealers = resp.data.data
                }).catch(() => {
                    this.dealers = []
                })
            }
        },
        methods: {
            fetchPage({ page, perPage, filters, sortOption, order }) {
                return partnerService.getPartnersByFilter(page, filters, sortOption, order, perPage)
            },
            deletePartner: id => partnerService.deletePartnerById(id),
            categoryTitle(category) {
                if (!category) {
                    return ''
                }
                return typeof category === 'object' ? category.title : category
            },
            vatNumbers(vats) {
                return (vats || []).map(vat => vat.number).join(', ')
            },
            toggleApprove(partner) {
                this.togglingId = partner.id
                partnerService.toggleApproved(partner.id).then(() => {
                    partner.approved = !partner.approved
                    this.$notify.success(`${partner.title} is now ${partner.approved ? 'approved' : 'pending'}`)
                }).catch(error => {
                    this.$notify.error(error, 'Could not change the approval')
                }).finally(() => {
                    this.togglingId = null
                })
            }
        }
    }
</script>

<style scoped>
.address {
    max-width: 260px;
}
</style>
